
import React, { useState, useEffect } from 'react';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { Websites } from './pages/Websites';
import { Editor } from './pages/Editor';
import { Marketing } from './pages/Marketing';
import { Clients } from './pages/Clients';
import { Website, Tenant, Lead } from './types';

const STORAGE_KEY_SITES = 'webflow_ai_sites';
const STORAGE_KEY_LEADS = 'webflow_ai_leads';

const INITIAL_WEBSITES: Website[] = [
  {
    id: 'site-1',
    tenantId: 'tenant-1',
    name: 'Tech Startup Portfolio',
    domain: 'tech-startup.com',
    primaryColor: '#6366f1',
    secondaryColor: '#8b5cf6',
    pages: [
      {
        name: 'Home',
        path: '/',
        sections: [
          {
            id: 's1',
            type: 'hero',
            content: {
              html: `<section style="padding: 100px 20px; text-align: center; background: linear-gradient(to bottom right, #f8fafc, #eff6ff); border-radius: 40px; margin-bottom: 40px; border: 1px solid #e2e8f0;">
                <h1 style="font-size: 3.5rem; font-weight: 900; color: #1e293b; letter-spacing: -0.05em; margin-bottom: 24px; font-family: sans-serif;">Revolutionize Your Workflow</h1>
                <p style="font-size: 1.25rem; color: #64748b; max-width: 700px; margin: 0 auto 40px; line-height: 1.6; font-family: sans-serif;">The all-in-one platform for high-performance teams to build, ship, and scale products faster than ever.</p>
                <div style="display: flex; gap: 16px; justify-content: center;">
                  <a href="#" style="background: #6366f1; color: white; padding: 18px 36px; border-radius: 20px; font-weight: 800; text-decoration: none; font-family: sans-serif; box-shadow: 0 20px 25px -5px rgba(99, 102, 241, 0.2);">Get Started Free</a>
                </div>
              </section>`,
              title: 'Revolutionize Your Workflow',
              subtitle: 'The all-in-one platform for high-performance teams to build, ship, and scale products faster than ever.',
              buttonText: 'Get Started Free',
              imageUrl: 'https://picsum.photos/800/600?random=1'
            }
          }
        ]
      }
    ],
    marketing: {
      promoBars: [
        { id: 'PROMO-BAR-99', isActive: true, text: '🎉 Summer Sale: 20% off all plans!', link: '/pricing', color: '#6366f1' }
      ],
      popups: [
        { id: 'FLYER-WIN-2024', isActive: false, title: 'Welcome Gift!', description: 'Sign up today for a free ebook.', imageUrl: 'https://picsum.photos/400/300', activeCampaign: 'Onboarding 2024' }
      ],
      games: []
    }
  }
];

const INITIAL_LEADS: Lead[] = [];

const CURRENT_TENANT: Tenant = {
  id: 'tenant-1',
  name: 'Acme Agency',
  plan: 'Pro'
};

const App: React.FC = () => {
  const [activePage, setActivePage] = useState('dashboard');
  const [websites, setWebsites] = useState<Website[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load from Storage on Mount
  useEffect(() => {
    const savedSites = localStorage.getItem(STORAGE_KEY_SITES);
    const savedLeads = localStorage.getItem(STORAGE_KEY_LEADS);

    if (savedSites) {
      setWebsites(JSON.parse(savedSites));
    } else {
      setWebsites(INITIAL_WEBSITES);
    }

    if (savedLeads) {
      setLeads(JSON.parse(savedLeads));
    } else {
      setLeads(INITIAL_LEADS);
    }
    
    setIsInitialized(true);
  }, []);

  // Save to Storage on Change
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem(STORAGE_KEY_SITES, JSON.stringify(websites));
    }
  }, [websites, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem(STORAGE_KEY_LEADS, JSON.stringify(leads));
    }
  }, [leads, isInitialized]);

  const handleUpdateSite = (updated: Website) => {
    setWebsites(prev => prev.map(s => s.id === updated.id ? updated : s));
  };

  const handleAddSite = (newSite: Website) => {
    setWebsites(prev => [...prev, newSite]);
  };

  const handleDeleteSite = (id: string) => {
    setWebsites(prev => prev.filter(s => s.id !== id));
    if (editingSiteId === id) setEditingSiteId(null);
  };

  const handleAddLead = (newLead: Lead) => {
    setLeads(prev => [newLead, ...prev]);
  };

  const handleUpdateLeadStatus = (leadId: string, status: Lead['status']) => {
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status } : l));
  };

  const editingSite = websites.find(s => s.id === editingSiteId);

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-400 font-bold text-sm uppercase tracking-widest">Loading Your Projects...</p>
        </div>
      </div>
    );
  }

  if (editingSiteId && editingSite) {
    return (
      <Editor 
        website={editingSite} 
        onUpdate={handleUpdateSite} 
        onBack={() => setEditingSiteId(null)}
        onCaptureLead={handleAddLead}
      />
    );
  }

  const renderContent = () => {
    switch (activePage) {
      case 'dashboard': return <Dashboard />;
      case 'websites': 
        return <Websites 
          websites={websites} 
          onSelectSite={(s) => setEditingSiteId(s.id)} 
          onAddSite={handleAddSite} 
          onDeleteSite={handleDeleteSite} 
        />;
      case 'marketing': return <Marketing websites={websites} onUpdateSite={handleUpdateSite} />;
      case 'clients': return <Clients leads={leads} onUpdateLeadStatus={handleUpdateLeadStatus} />;
      default: return <Dashboard />;
    }
  };

  return (
    <Layout activePage={activePage} onNavigate={setActivePage} tenantName={CURRENT_TENANT.name}>
      {renderContent()}
    </Layout>
  );
};

export default App;
